import { Component, computed, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { debounceTime } from 'rxjs/operators'
import { MapGeoJSONFeature } from 'maplibre-gl'
import { MapEventsService } from 'src/app/core/services/map-events.service'
import { Layers } from 'src/app/shared/layers-config/layers'
import { DetailsModel } from 'src/app/shared/models/details.model'
import { AppPaths } from 'src/app/app-routing.module'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { RoadSpeedSegmentPropertiesModel } from 'src/app/shared/models/road-speed-segment-properties.model'
import { RolloutPropertiesModel } from 'src/app/shared/models/rollouts-properties.model'
import { getTimeZoneLabel } from 'src/app/shared/utils/date.utils'
import { format } from 'date-fns'

@Component({
  selector: 'app-rollouts-map-tile-segment-details',
  templateUrl: './rollouts-map-tile-segment-details.component.html',
  standalone: false,
})
export class RolloutsTileSegmentDetailsComponent {
  public readonly details = signal(new Map<string, DetailsModel>())
  public readonly features = signal<MapGeoJSONFeature[]>([])
  public readonly index = signal(0)
  public readonly zoomLevel = signal<number | undefined>(undefined)
  public readonly rawFeature = computed(() =>
    JSON.stringify(this.features()[this.index()], undefined, 2),
  )

  constructor(
    private readonly mapEventService: MapEventsService,
    public appState: AppStateService,
  ) {
    this.mapEventService.featureClick
      .pipe(takeUntilDestroyed())
      .pipe(debounceTime(50))
      .subscribe((features: MapGeoJSONFeature[]) => {
        this.clearDetails()
        if (this.features().length !== features.length) this.index.set(0)
        if (features[this.index()].layer.id === `${Layers.RolloutsTile.name}-segments`) {
          this.features.set(features)
          this.setDetails()
        }
      })
  }

  toggleFeature(index: number) {
    this.index.set(index)
    this.setDetails()
  }

  setDetails() {
    this.zoomLevel.set(this.appState.getState().zoom)
    const feature = this.features()[this.index()]
    const properties: RolloutPropertiesModel = feature.properties as RolloutPropertiesModel
    this.mapMetaToDetails(properties)
    this.appState.setState(AppPaths.Customer, {
      selectedFeatureId: feature.id?.toString() ?? '',
    })
  }

  private mapMetaToDetails(properties: RolloutPropertiesModel) {
    this.setDetail('Meta', {
      title: 'Rollout Segment',
      items: [
        {
          description: 'Id',
          value: properties.signId ?? '',
        },
        {
          description: 'Main Type',
          value: properties.mainType ?? '',
        },
        {
          description: 'Highway',
          value: properties.highway ?? '',
        },
        {
          description: 'Direction',
          value: properties.direction ?? '',
        },
        {
          description: 'Probability',
          value: properties.probability?.toString() ?? '',
        },
        {
          description: 'Speed Limit',
          value: properties.speedLimit?.toString() ?? '',
        },
        {
          description: 'Country Code',
          value: properties.countryCode ?? '',
        },
        {
          description: `CreationTime(${getTimeZoneLabel(new Date(properties.creationTime))})`,
          value: `${
            properties.creationTime
              ? format(new Date(properties.creationTime), 'yyyy-MM-dd HH:mm')
              : '-'
          }`,
        },
        {
          description: `UpdateTime(${getTimeZoneLabel(new Date(properties.updateTime))})`,
          value: `${
            properties.updateTime
              ? format(new Date(properties.updateTime), 'yyyy-MM-dd HH:mm')
              : '-'
          }`,
        },
        {
          description: 'Display Type',
          value: properties.displayType ?? '',
        },

        {
          description: 'Submitter',
          value: properties.submitter ?? '',
        },
        {
          description: 'Supplemental Signs',
          value: properties.supplementalSigns ?? '',
        },
        {
          description: 'Unit',
          value: properties.unit ?? '',
        },
        {
          description: 'Version',
          value: properties.version?.toString() ?? '',
        },
      ],
    })
  }

  close() {
    this.clearDetails()
    this.appState.setState(AppPaths.Customer, {
      selectedFeatureId: undefined,
    })
  }

  private setDetail(key: string, value: DetailsModel): void {
    this.details.update((details) => new Map(details).set(key, value))
  }

  private clearDetails(): void {
    this.details.set(new Map())
  }
}
